/** Audit history for a meter/transformer detail panel (UX C4, spec 3.11).
 *  Read-only: newest-first entity_change_log rows (limit 20 server-side).
 *  Unknown actions render generically — the ledger's vocabulary is open
 *  (e.g. 'bulk_fill_authorized'). Renders nothing when there is no history,
 *  matching the degrade-gracefully idiom of the sibling sections. */
import { useQuery } from '@tanstack/react-query'
import { getMeterHistory, getTransformerHistory } from '../../api/topology.ts'
import { actionLabel, changeSummary } from './historyFormat.ts'

export function HistorySection({
  entityType,
  id,
}: {
  entityType: 'meter' | 'transformer'
  id: number
}) {
  const { data: history } = useQuery({
    queryKey: ['entity-history', entityType, id],
    queryFn: () =>
      entityType === 'meter' ? getMeterHistory(id) : getTransformerHistory(id),
  })

  if (!history || history.length === 0) return null

  return (
    <div className="border-t border-gray-200 pt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        History
      </p>
      <ul className="space-y-1.5 max-h-40 overflow-y-auto">
        {history.map((h, i) => {
          const change = changeSummary(h)
          return (
            <li
              key={i}
              className="text-xs text-gray-600"
              title={h.detail ? JSON.stringify(h.detail) : undefined}
            >
              <div className="flex justify-between gap-2">
                <span className="truncate">
                  {actionLabel(h.action)}
                  {h.changed_by ? ` · by ${h.changed_by}` : ''}
                </span>
                <span className="text-gray-400 whitespace-nowrap">
                  {new Date(h.changed_at).toLocaleString()}
                </span>
              </div>
              {/* What actually changed — the reason the row is worth showing. */}
              {change && <div className="text-gray-500">{change}</div>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
